import type { ComponentProps, ReactNode } from "react";

import { useYMaps } from "@features/map/providers/yandex-maps-context";

import { BrandIcon } from "@shared/ui/brand-icon";
import { Button } from "@shared/ui/button";
import type { IconNameType } from "@shared/ui/icon";
import { Body } from "@shared/ui/typography";
import { parseYandexCoords } from "@shared/utils";

import s from "./point.module.scss";

export type CompProps = ComponentProps<"div"> & {
  coordinates: string | [number, number];
  title: string;
  onClick?: () => void;
  href?: string;
  target?: string;
  type: string;
  icon?: IconNameType;
  customIcon?: ReactNode;
  isOpen?: boolean;
};

export const Point = (props: CompProps) => {
  const {
    coordinates,
    title,
    onClick,
    href,
    target,
    type,
    icon,
    customIcon,
    isOpen = false,
  } = props;

  const { YMapMarker } = useYMaps();

  if (!YMapMarker || !coordinates) return null;

  let parsedCoordinates: [number, number] | null = null;
  if (Array.isArray(coordinates) && coordinates.length === 2) {
    parsedCoordinates = coordinates;
  } else {
    try {
      parsedCoordinates = parseYandexCoords(String(coordinates)) as [
        number,
        number,
      ];
    } catch (e) {
      console.warn("[Point] failed to parse coordinates", {
        coordinates,
        title,
        error: e,
      });
      return null;
    }
  }

  // Only add cursor pointer if there's an onClick or href
  const hasInteraction = onClick || href;

  return (
    <YMapMarker coordinates={parsedCoordinates}>
      <Button
        className={s.point}
        onClick={onClick}
        href={href}
        target={target}
        data-open={isOpen}
        style={{ cursor: hasInteraction ? "pointer" : "default" }}
      >
        <div className={s.pointWrapper}>
          <div className={s.pointIconContainer}>
            {customIcon
              ? customIcon
              : icon && (
                  <BrandIcon
                    name={icon}
                    fillColor="bistre"
                    strokeColor="milk"
                    className={s.pointIcon}
                  />
                )}
          </div>
          <div className={s.pointText}>
            <Body size="caption" weight="semiBold">
              {title}
            </Body>
            <Body size="caption-small" className={s.pointType}>
              {type}
            </Body>
          </div>
        </div>
      </Button>
    </YMapMarker>
  );
};

Point.displayName = "Point";
